import React from "react";
import PropTypes from "prop-types";
import moment from "moment-timezone";
import i18next from "i18next";

import { DataTypeDisplayValue } from "../DataTypeDisplayValue";
import EntityImageGallery from "./entity_image_gallery";
import DeviceViewDetailsGraph from "../Common/device_view_details_graph";
import { CalibrationSection } from "../Device";

import config from "config";

const EntityDetail = ({ entity, isMobile }) => {    
    const measurementDataTypes = entity.currentMeasurements
                    .filter(cm => cm.lastReceivedData != null)
                    .map(cm => {
                        const dataType = {...(cm.lastReceivedData.deviceDataType)};
                        if (entity.entityTypeToken == 'asset') {
                            dataType.name = `${cm.originatingMeasureName} : ${dataType.name}`;
                            dataType.startTime = cm.startTime;
                            dataType.endTime = cm.endTime;
                        }
                        return { datatype: dataType, currentMeasurement: cm };
                    });

    if (isMobile) {
        return <MobileView entity={entity} measurementDataTypes={measurementDataTypes} />;
    } else {
        return <DesktopView entity={entity} measurementDataTypes={measurementDataTypes} />;
    }
};

EntityDetail.propTypes = {
    isMobile: PropTypes.bool.isRequired,
    entity: PropTypes.object.isRequired
};

const renderFromNowTime = (time, timezone) => {
    let timeDate = timezone ? moment(time).tz(timezone) : moment(time);
    return timeDate.fromNow();
};

const fetchCalibrationDate = (entity) => {
    const calibrationDate = entity.currentMetadata?.filter((dm) => dm.metadataTypeToken == "calibration_effective_at");
    return calibrationDate != null && calibrationDate.length > 0 ? calibrationDate[0].numericValue : null;
};

const DesktopView = ({ entity, measurementDataTypes }) => {
    let calibrationImageSrc = null;
    if (entity.calibrationImages && Object.keys(entity.calibrationImages).length > 0) {
        const esn = Object.keys(entity.calibrationImages)[0];
        calibrationImageSrc = `${config.API_BASE_URL}entities/${esn}/images/${entity.calibrationImages[esn]}`;
    }

    return (
        <div className="device-detail-overlay">
            <div className="left-side">
                <div>
                    <label>{i18next.t("pages.devices.status")}</label>
                    <p style={{ marginBottom: "30px" }}>
                        <EntityHealth entity={entity} measurementDataTypes={measurementDataTypes} />
                    </p>
                </div>

                { 
                    calibrationImageSrc && 
                    <CalibrationSection 
                        imageSrc={calibrationImageSrc}
                        calibrationDate={fetchCalibrationDate(entity)}
                        timezone={entity.businessGroup?.timezone}
                        inSidebar={true}
                    />
                }

                <div>
                    <label>{i18next.t("pages.devices.last-updated")}</label>
                    <p style={{ marginBottom: "30px" }}>
                        <img width={"16"} height={"16"} style={{ marginRight: "3px" }} src={`${config.WEB_BASE_URL}/images/icon/time.svg`} />
                        &nbsp;
                        {renderFromNowTime(entity.lastUpdatedAt, entity.businessGroup?.timezone)}
                    </p>
                </div>

                <div>
                    <label>{i18next.t("pages.devices.location")}</label>
                    <p style={{ marginBottom: "30px" }}>
                        <img width={"16"} height={"16"} style={{ marginRight: "3px" }} src={`${config.WEB_BASE_URL}/images/icon/location_circle.svg`} />
                        &nbsp;
                        {entity.businessGroup.name}
                    </p>
                </div>

                <div>
                    <label>{i18next.t("pages.devices.unresolved-events")}</label>
                    <p style={{ marginBottom: "30px" }}>{entity.unresolvedEventCount}</p>
                </div>

                { entity.areaTag &&
                    <div>
                        <label>{i18next.t("pages.devices.area")}</label>
                        <p style={{ marginBottom: "30px" }}>
                            <img width={"16"} height={"16"} style={{ marginRight: "3px" }} src={`${config.WEB_BASE_URL}/images/icon/area.svg`} />
                            &nbsp;
                            {entity.areaTag.name}
                        </p>
                    </div>
                }

                <TagArea tags={entity.tags} />
            </div>

            <div className="right-side">
                <div style={{ marginBottom: "10px", borderBottom: "1px solid rgb(216, 215, 215)" }}>
                    <label style={{ display: "inline-block", fontSize: "16px", color: "#1f627b", marginBottom: "0px" }}>{i18next.t("pages.devices.graph")}</label>
                </div>

                {measurementDataTypes.length > 0 && (
                    <DeviceViewDetailsGraph
                        detailOverlay={true}
                        dataTypes={measurementDataTypes}
                        dataType={measurementDataTypes[0].dataType}
                        entityTypeToken={entity.entityTypeToken}
                        entityId={entity.entityId}
                        timezone={entity.businessGroup?.timezone}
                    />
                )}

                <div className="image-header">
                    <label style={{ color: "#1f627b", fontSize: "16px", borderBottom: "1px solid rgb(216, 215, 215)" }}>{i18next.t("pages.devices.images")}</label>
                    <EntityImageGallery serialNumber={entity.serialNumber} images={entity.images} />
                </div>
            </div>
        </div>
    );
};

DesktopView.propTypes = {
    measurementDataTypes: PropTypes.array.isRequired,
    entity: PropTypes.object.isRequired,
};

const MobileView = ({ entity, measurementDataTypes }) => (
    <div className="device-detail-overlay">
        <div className="left-side-mobile">
            <div>
                <label>{i18next.t("pages.devices.status")}</label>
                <p style={{ marginBottom: "30px" }}>
                    <img width={"16"} height={"16"} src={`${config.WEB_BASE_URL}/images/icon/sensor_${entity.statusToken}.svg`} />
                    &nbsp;
                    {i18next.t(`device-statuses.${entity.statusToken}`)}
                    &nbsp;/&nbsp;
                    {i18next.t(`connectivity-status-data-string-display.${entity.online ? "Online" : "Offline"}`)}
                </p>
            </div>

            { entity.calibrationImages?.length > 0 && <CalibrationSection imageSrc={`${config.API_BASE_URL}entities/${entity.serialNumber}/images/${entity.calibrationImages[0]}`} /> }

            <div>
                <label>{i18next.t("pages.devices.unresolved-events")}</label>
                <p style={{ marginBottom: "30px" }}>{entity.unresolvedEventCount}</p>
            </div>

            <div>
                <label>{i18next.t("pages.devices.location")}</label>
                <p style={{ marginBottom: "30px" }}>
                    <img width={"16"} height={"16"} style={{ marginRight: "3px" }} src={`${config.WEB_BASE_URL}/images/icon/line_category.svg`} />
                    &nbsp;
                    {entity.businessGroup.name}
                </p>
            </div>
        </div>

        <div className="right-side-mobile">
            <div>
                <label>{i18next.t("pages.devices.last-updated")}</label>
                <p style={{ marginBottom: "30px" }}>
                    <img width={"16"} height={"16"} style={{ marginRight: "3px" }} src={`${config.WEB_BASE_URL}/images/icon/time.svg`} />
                    &nbsp;
                    {renderFromNowTime(entity.lastUpdatedAt, entity.businessGroup?.timezone)}
                </p>
            </div>

            <div>
                <label>{i18next.t("pages.devices.area")}</label>
                <p style={{ marginBottom: "30px" }}>
                    <img width={"16"} height={"16"} style={{ marginRight: "3px" }} src={`${config.WEB_BASE_URL}/images/icon/area.svg`} />
                    &nbsp;
                    {entity.areaTag?.name}
                </p>
            </div>
        </div>

        <TagArea tags={entity.tags} />

        <div style={{ marginBottom: "10px", borderBottom: "1px solid rgb(216, 215, 215)" }}>
            <label style={{ display: "inline-block", fontSize: "16px", color: "#1f627b", marginBottom: "0px" }}>{i18next.t("pages.devices.graph")}</label>
        </div>

        {measurementDataTypes.length > 0 && (
            <DeviceViewDetailsGraph
                detailOverlay={true}
                dataTypes={measurementDataTypes}
                dataType={measurementDataTypes[0].dataType}
                entityTypeToken={entity.entityTypeToken}
                entityId={entity.entityId}
                timezone={entity.businessGroup?.timezone}
            />
        )}

        <div className="image-header">
            <label style={{ color: "#1f627b", fontSize: "16px", borderBottom: "1px solid rgb(216, 215, 215)" }}>{i18next.t("pages.devices.images")}</label>
            <EntityImageGallery serialNumber={entity.serialNumber} images={entity.images} />
        </div>
    </div>
);

MobileView.propTypes = {
    measurementDataTypes: PropTypes.array.isRequired,
    entity: PropTypes.object.isRequired,
};

const EntityHealth = ({ entity, measurementDataTypes }) => {
    const batteryValue = measurementDataTypes.find(mdt => ['battery', 'battery_percentage', 'lora_battery_level'].includes(mdt.datatype.token));
    const rssiValue = measurementDataTypes.find(mdt => ['rssi', 'cell_rssi', 'wifi_client_rssi'].includes(mdt.datatype.token));

    return (
      <p>
        <p>
            <img width={"16"} height={"16"} src={`${config.WEB_BASE_URL}/images/icon/sensor_${entity.statusToken}.svg`} />
            &nbsp;
            {i18next.t(`device-statuses.${entity.statusToken}`)}
            &nbsp;/&nbsp;
            {i18next.t(`connectivity-status-data-string-display.${entity.online ? "Online" : "Offline"}`)}
        </p>

        {
            entity.online && batteryValue && 
            <p>
                <img width={"16"} height={"16"} style={{ marginRight: "3px" }} src={`${config.WEB_BASE_URL}/images/icon/battery_level.svg`} />
                <DataTypeDisplayValue value={batteryValue.currentMeasurement.lastReceivedData} />         
            </p>
        }

        {
            entity.online && rssiValue && 
            <p>
                <img width={"16"} height={"16"} style={{ marginRight: "3px" }} src={`${config.WEB_BASE_URL}/images/icon/line_category.svg`} />
                <DataTypeDisplayValue value={rssiValue.currentMeasurement.lastReceivedData} />         
            </p>
        }        
      </p>
    );
};

EntityHealth.propTypes = {
    measurementDataTypes: PropTypes.array.isRequired,
    entity: PropTypes.object.isRequired,
};

const TagArea = ({ tags }) => (
    <div>
        <label>{i18next.t("pages.devices.tags")}</label>
        {tags.length > 0 ? (
            <div className="selected-tags" style={{ marginBottom: "10px" }}>
                {tags.map((tag, index) => (
                    <div className="selected-tag" key={index}>
                        <img className="tag-icon" src={`${config.WEB_BASE_URL}/images/icon/${tag.tagType.token === "area" ? "area_transparent.svg" : "tag.svg"}`} height="16" />
                        <span className="selected-tag-text">{tag.name}</span>
                    </div>
                ))}
            </div>
        ) : (<span>None</span>)}
    </div>
);

TagArea.propTypes = {
    tags: PropTypes.array.isRequired
};

export default EntityDetail;